import { Component, effect, inject, OnDestroy, OnInit } from '@angular/core';
import {CommonModule} from '@angular/common';
import {AuthService} from '@lib-ui/auth/auth.service';
import { IOrderDataUpdate} from '@lib-typing/order.interface';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {OrdersService} from '@lib-services/orders.service';
import {TaskCardComponent} from '@lib-ui/task-card/task-card.component';
import {Params, Router} from '@angular/router';
import {MessageService} from '@lib-services/message.service';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {OrderStatusEnum} from '@libs-enums/status.enum';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {OrderTypeEnum} from '@libs-enums/executorOrderType';
import {AssignOrdersService} from '@lib-services/assigns-orders.service';
import {GeolocationService} from '@executor/src/app/services/geolocation.service';
import {IShortLocation} from '@lib-typing/location.interface';
import {MatFormField, MatLabel, MatSuffix} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {setDayRange} from '../../../../../../libs/utils/date-format';
import {Order} from '@libs-classes/order.class';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    Observable,
    of,
    Subject, Subscription,
    switchMap,
    takeUntil,
} from 'rxjs';
import {DateService} from '@executor/src/app/services/date.service';
import {PartSearchService} from '@lib-services/part-search.service';
import { OfflineService } from '@lib-ui/offline/offline.service';
import { OfflineStorageService } from '@lib-ui/offline/OfflineStorageService.service';

@Component({
    selector: 'app-orders-list',
    standalone: true,
    imports: [
        CommonModule,
        TaskCardComponent,
        MatIconButton,
        MatIcon,
        MatLabel,
        MatButton,
        MatProgressSpinner,
        MatFormField,
        MatInput,
        ReactiveFormsModule,
        MatSuffix],
    templateUrl: './orders-list.component.html',
    styleUrl: './orders-list.component.scss'
})
export class OrdersListComponent implements OnInit, OnDestroy {
    orders: Order[] = [];
    loading = false;
    dateControl  = new FormControl(Date.now());
    orderType = OrderTypeEnum.ACTUAL;
    timeControl = new FormControl('');
    addressControl = new FormControl('');
    protected authService = inject(AuthService);
    protected ordersService = inject(OrdersService);
    protected messageService = inject(MessageService);
    private assignOrdersService = inject(AssignOrdersService);
    private geolocationService = inject(GeolocationService);
    private partSearchService = inject(PartSearchService);
    private offlineService = inject(OfflineService);
    private dateService = inject(DateService);
    private offlineStorageService = inject(OfflineStorageService);
    protected router = inject(Router);
    protected readonly OrderStatusEnum = OrderStatusEnum;
    protected readonly OrderType = OrderTypeEnum;
    unsubscribe$ = new Subject<void>();

    constructor() {
        effect(() => {
            if (this.authService.user().data.id) {
                this.initializeOrderFetching();
            }
        });

        effect(() => {
            this.dateControl.setValue(this.dateService.currentDate().getTime());
        });
    }

    ngOnInit(): void {
        if (this.offlineService.offline()) {
            return;
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    private initializeOrderFetching(): void {
        this.getOrders(OrderTypeEnum.ACTUAL);
        this.setupControlSubscriptions();
    }

    private setupControlSubscriptions(): void {
        this.dateControl.valueChanges.subscribe(() => this.getOrders(this.orderType));

        const activeOrderControls = [this.timeControl, this.addressControl];
        activeOrderControls.forEach(control => {
            control.valueChanges.pipe(
                debounceTime(1000),
                distinctUntilChanged(),
                filter(() => this.orderType === OrderTypeEnum.ACTIVE)
            ).subscribe(() => this.getOrders(OrderTypeEnum.ACTIVE));
        });
    }

    getOrders(type: OrderTypeEnum): void {
        this.orderType = type;
        if (this.offlineService.offline()) {
            this.offlineService.ordersList$.pipe(
                takeUntil(this.unsubscribe$),
                switchMap((orders) => {
                    return type !== OrderTypeEnum.ACTIVE ?
                        this.filterAndSortOrders(orders.filter(order => order.data.location), type) : of([]);
                }),
            ).subscribe((orders) => this.orders = orders);
            ////кода offline Нераспределенные заявки игнорируются
            return;
        }

        if(this.offlineStorageService.updateRequest()) {
            /// сначала обновляем все Request сохраненные, а затем только получаем все orders
            return;
        }

        this.loading = true;
        const params: Params = this.buildOrderParams(type);

        this.ordersService.getAllOrders(this.createSearchParams(params))
            .pipe(
                switchMap(orders => {
                    if (type !== OrderTypeEnum.ACTIVE) {
                        this.offlineService.ordersList$.next(orders);
                        ///для обеления в кеш все кроме нераспределенных заказов
                        this.offlineService.loadDateOrderOffline$.next();
                    }
                    return this.filterAndSortOrders(orders.filter(order => order.data.location), type).pipe(
                        catchError(error => {
                            this.messageService.errorMessage(error); // Обрабатываем ошибку
                            return of(orders); // Возвращаем данные, которые были до ошибки
                        })
                    );
                }),
            )
            .subscribe({
                next: orders => this.orders = orders,
                error: (err) => this.messageService.errorMessage(err.error.message || 'Произошла ошибка')
            }).add(() => this.loading = false);
    }

    private buildOrderParams(type: OrderTypeEnum): Params {
        const params: Params = {
            dateFrom: setDayRange(this.dateControl.value as number),
        };

        if (type === OrderTypeEnum.ACTIVE) {
            if (this.timeControl.value) {
                params['dateFrom'] = this.setDateTimeRange(this.dateControl.value as number, this.timeControl.value);
            }
            if (this.addressControl.value) {
                params['addressDetails'] = this.addressControl.value;
            }
            params['status'] = OrderStatusEnum.active;
            params['group.id'] = this.authService.user().data.group.id;
        } else {
            params['executor.id'] = this.authService.user().data.id;
        }

        return params;
    }

    private filterAndSortOrders(orders: Order[], type: OrderTypeEnum): Observable<Order[]> {
        switch (type) {
        case OrderTypeEnum.ACTUAL:
            return of(orders.filter(order => [OrderStatusEnum.ready, OrderStatusEnum.atwork, OrderStatusEnum.onway].includes(order.data.status))
                .sort((a, b) => (a.data.queueNumber ?? 0) - (b.data.queueNumber ?? 0))
            );
        case OrderTypeEnum.ACTIVE:
            return this.sortOrdersByProximity(orders);
        case OrderTypeEnum.INACTIVE:
            return of(orders.filter(order => [OrderStatusEnum.done, OrderStatusEnum.reject].includes(order.data.status)));
        default:
            return of([]);
        }
    }

    private sortOrdersByProximity(orders: Order[]): Observable<Order[]> {
        return this.geolocationService.getCurrentPosition().pipe(
            map(position => {
                const executorLocation: IShortLocation = {
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                };
                return orders.sort((a, b) => {
                    // Сначала сравниваем по расстоянию
                    const distanceA = this.assignOrdersService.calculateDistance(executorLocation, a.data.location);
                    const distanceB = this.assignOrdersService.calculateDistance(executorLocation, b.data.location);
                    const distanceDiff = distanceA - distanceB;

                    // Если расстояния равны, сортируем по dateFrom
                    if (distanceDiff === 0) {
                        return new Date(a.data.dateFrom).getTime() - new Date(b.data.dateFrom).getTime();
                    }

                    return distanceDiff;
                });
            }),
        );
    }

    openOrderForm(id: number): void {
        this.router.navigate(['order-executor', id]);
    }

    openYandexMaps(event: Event, latitude: number, longitude: number): void {
        event.stopImmediatePropagation();
        const yandexMapsUrl = `yandexnavi://build_route_on_map?lat_to=${latitude}&lon_to=${longitude}`;
        const webYandexMapsUrl = `https://yandex.ru/maps/?pt=${longitude},${latitude}&z=16`;

        if (this.isMobile()) {
            // Пытаемся открыть приложение
            window.location.href = yandexMapsUrl;
        } else {
            window.open(webYandexMapsUrl, '_blank');
        }
    }

    assignOrderToMe(event: Event, order: Order): void {
        event.stopImmediatePropagation();
        this.messageService.confirmMessage({
            title: 'Вы действительно хотите взять заявку в работу?',
        }).pipe(
            filter(confirm => confirm),
            switchMap(() => {
                // Загружаем все заказы исполнителя на текущий день
                const query: Params = {
                    'filter.executor.id': this.authService.user().data.id,
                    'filter.dateFrom': setDayRange(new Date(order.data.dateFrom))
                };

                return this.ordersService.getAllOrders(query).pipe(
                    map(allOrders => {
                        return this.setNewQueue(allOrders, order);
                    })
                );
            }),
            switchMap(ordersForUpdate => this.ordersService.updateOrders(ordersForUpdate))
        ).subscribe({
            next: () => {
                this.orderType = OrderTypeEnum.ACTUAL;
                this.getOrders(this.orderType);
            },
            error: () => this.messageService.errorMessage('Произошла ошибка при назначении заявки'),
        });
    }

    // Метод для проверки, используется ли мобильное устройство
    isMobile(): boolean {
        return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    }

    setDateTimeRange(date: number, time: string): string {
        const selectedDate = new Date(date);
        const [hours, minutes] = time.split(':').map(Number);
        // Получаем смещение часового пояса в минутах
        const timezoneOffset = selectedDate.getTimezoneOffset();
        // Создаем дату в UTC, учитывая смещение часового пояса
        const startDateTime = new Date(Date.UTC(
            selectedDate.getFullYear(),
            selectedDate.getMonth(),
            selectedDate.getDate(),
            hours,
            minutes + timezoneOffset,
            0
        ));

        // Конец дня также учитывает смещение часового пояса
        const endDateTime = new Date(Date.UTC(
            selectedDate.getFullYear(),
            selectedDate.getMonth(),
            selectedDate.getDate(),
            23,
            59 + timezoneOffset,
            59
        ));
        return `$btw:${startDateTime.toISOString()},${endDateTime.toISOString()}`;
    }

    createSearchParams(config: Params): Params {
        let searchParams: Params = {
            sortBy: 'queueNumber:ASC',
        };
        if (config) {
            let params: string[];
            Object.entries(config).forEach(([key, value]) => {
                if (value) {
                    let filterValue;
                    switch (key) {
                    case 'addressDetails':
                        params = value.split(' ').map((el: string) => el.trim());
                        filterValue = [`$ilike:${params[0]}`].concat(this.partSearchService.createMultiStringParams(params.slice(1)));
                        break;
                    default:
                        filterValue = `${value}`;
                    }
                    searchParams = {
                        ...searchParams,
                        [`filter.${key}`]: filterValue,
                    };
                }
            });
        }
        return searchParams;
    }
    // переназначаем очередь после того, как работник взял себе заявку из нераспределенных
    setNewQueue(allOrders: Order[], newOrder: Order): IOrderDataUpdate[] {
        const currentHour = new Date().getHours();

        // Сортируем заказы по номеру очереди
        const sortedOrders = [...allOrders].sort((a, b) => a.data.queueNumber! - b.data.queueNumber!);

        // Находим индекс, куда вставить новую заявку (первая заявка со статусом "ready")
        const insertIndex = sortedOrders.findIndex(order => order.data.status === OrderStatusEnum.ready);

        // Вставляем новую заявку в массив
        sortedOrders.splice(insertIndex === -1 ? sortedOrders.length : insertIndex, 0, newOrder);
        const currentIndex = sortedOrders.findIndex(order => order.data.id === newOrder.data.id);
        const prevOrder = sortedOrders[currentIndex - 1];
        const nextOrder = sortedOrders[currentIndex + 1];

        if(prevOrder && new Date(prevOrder.data.dateFrom).getHours() < currentHour && new Date(prevOrder.data.dateTo).getHours() > currentHour) {
            sortedOrders[currentIndex].data.dateFrom = prevOrder.data.dateFrom;
            sortedOrders[currentIndex].data.dateTo = prevOrder.data.dateTo;
        } else if(nextOrder && new Date(nextOrder.data.dateFrom).getHours() < currentHour && new Date(nextOrder.data.dateTo).getHours() > currentHour) {
            sortedOrders[currentIndex].data.dateFrom = nextOrder.data.dateFrom;
            sortedOrders[currentIndex].data.dateTo = nextOrder.data.dateTo;
        } else {
            sortedOrders[currentIndex].data.dateFrom = new Date(new Date().setHours(currentHour));
            sortedOrders[currentIndex].data.dateTo = new Date(new Date().setHours(currentHour + 1));
        }

        // Обновляем номера очереди и возвращаем обновленные данные
        return sortedOrders.map((order, index) => ({
            id: order.data.id,
            dateFrom: order.data.dateFrom,
            dateTo: order.data.dateTo,
            executorId: this.authService.user().data.id,
            status: order.data.status === OrderStatusEnum.active ? OrderStatusEnum.ready : order.data.status,
            queueNumber: index + 1,
        }));
    }
}
